/* Farmhouse Peace sale page v2 — Dawn compatible.
   All product amounts come from the real Shopify product variant data in the
   customer's active Market. NO additional discounts or price overrides.
   Only the featured purchase button adds the two items. All other CTAs jump there.
*/
(() => {
  'use strict';

  const twoDigits = (value) => String(Math.max(0, value)).padStart(2, '0');
  const validZone = (value) => /(?:Z|[+-]\d{2}:\d{2})$/i.test(value);

  function moneyFormatter(section, purchase = false) {
    const currency = section.dataset.fpsCurrency || 'CAD';
    const locale = (section.dataset.fpsLocale || 'fr').replace('_', '-');
    // Promotional text uses a short currency symbol. The purchase area keeps
    // the explicit Canadian-dollar marker, avoiding ambiguity at checkout.
    const displayLocale = purchase && currency === 'CAD' && locale.startsWith('fr') ? 'fr' : locale;
    try {
      const settings = { style: 'currency', currency, currencyDisplay: purchase ? 'symbol' : 'narrowSymbol' };
      const digits = new Intl.NumberFormat(displayLocale, settings).resolvedOptions().maximumFractionDigits;
      const whole = new Intl.NumberFormat(displayLocale, { ...settings, minimumFractionDigits: 0, maximumFractionDigits: digits });
      const fractional = new Intl.NumberFormat(displayLocale, { ...settings, minimumFractionDigits: digits, maximumFractionDigits: digits });
      return (cents) => {
        const amount = Number(cents) || 0;
        return (Math.round(Math.abs(amount)) % 100 === 0 ? whole : fractional).format(amount / 100);
      };
    } catch (_) {
      return (cents) => {
        const amount = Number(cents) || 0;
        const value = (amount / 100).toFixed(amount % 100 === 0 ? 0 : 2);
        const suffix = currency === 'CAD' ? (purchase ? '$CA' : '$') : currency === 'USD' ? '$' : currency === 'EUR' ? '€' : currency;
        return `${value} ${suffix}`;
      };
    }
  }

  function variant(section, which) {
    const picker = section.querySelector(`[data-fps-variant="${which}"]`);
    if (picker) {
      const option = picker.selectedOptions[0];
      if (!option) return { id: null, price: 0, compareAt: 0, available: false, image: '' };
      return {
        id: option.value,
        price: Number(option.dataset.price) || 0,
        compareAt: Number(option.dataset.compare || option.dataset.price) || 0,
        available: option.dataset.available === 'true',
        image: option.dataset.image || '',
        mediaId: option.dataset.mediaId || ''
      };
    }
    const key = which === 'main' ? 'Main' : 'Box';
    return {
      id: section.dataset[`fps${key}Id`] || null,
      price: Number(section.dataset[`fps${key}Price`]) || 0,
      compareAt: Number(section.dataset[`fps${key}Compare`]) || 0,
      available: section.dataset[`fps${key}Available`] === 'true',
      image: '',
      mediaId: ''
    };
  }

  function announcementDeadline(section, forceReset = false) {
    const mode = section.dataset.fpsCountdownMode || 'rolling';
    if (mode === 'off') return NaN;
    if (mode === 'fixed') {
      const iso = section.dataset.fpsDeadline || '';
      return validZone(iso) ? Date.parse(iso) : NaN;
    }
    // Rolling 24h default. A per-browser, persistent window for an explicitly
    // configured timed offer. Changing campaign key starts the next campaign.
    const hours = Math.max(1, Number(section.dataset.fpsRollingHours) || 24);
    const key = `fps-v2-timer:${section.dataset.fpsCampaignKey || 'default-offer'}`;
    const freshEnd = Date.now() + hours * 60 * 60 * 1000;
    try {
      let deadline = Number(window.localStorage.getItem(key));
      if (forceReset || !Number.isFinite(deadline) || deadline <= Date.now()) {
        deadline = freshEnd;
        window.localStorage.setItem(key, String(deadline));
      }
      return deadline;
    } catch (_) {
      return freshEnd;
    }
  }

  function init(section) {
    if (section.dataset.fpsInitialized === 'true') return;
    section.dataset.fpsInitialized = 'true';
    document.body.classList.add('fps-sale-active');

    const quantityOffer = section.dataset.fpsPurchaseMode !== 'fixed_bundle';
    const money = moneyFormatter(section);
    const purchaseMoney = moneyFormatter(section, true);
    const displayMoney = (element, cents) => element.closest('.fps-purchase-fields') ? purchaseMoney(cents) : money(cents);
    const buttons = Array.from(section.querySelectorAll('[data-fps-add]'));
    const status = section.querySelector('[data-fps-status]');
    const giftNote = section.querySelector('[data-fps-gift-note]');
    const root = (section.dataset.fpsRoot || window.Shopify?.routes?.root || '/').replace(/\/?$/, '/');
    let adding = false;
    let timerId;
    let reviewCleanup = () => {};

    function tell(message, type = '') {
      if (!status) return;
      status.textContent = message;
      status.dataset.kind = type;
    }

    // Bridge selected main-product options to the real Shopify variant ID.
    // The existing sale-page cart request continues to own add-to-cart behavior.
    function syncMainVariant(changedControl = null) {
      const controls = Array.from(section.querySelectorAll('[data-fps-main-option]'));
      const picker = section.querySelector('select[data-fps-variant="main"]');
      if (!picker || !controls.length) return;
      const candidates = Array.from(picker.options);
      const matches = (candidate, index) =>
        controls.every((control, i) => candidate.dataset[`option${i + 1}`] === control.value);
      let selected = candidates.find(matches);
      if (!selected && changedControl) {
        // If a nonexistent combination was chosen, stay within the last-changed
        // option and select a real, preferably available, variant instead.
        const changedIndex = controls.indexOf(changedControl);
        const related = candidates.filter((candidate) =>
          candidate.dataset[`option${changedIndex + 1}`] === changedControl.value);
        selected = related.find((candidate) => candidate.dataset.available === 'true') || related[0];
        if (selected) {
          controls.forEach((control, i) => {
            control.value = selected.dataset[`option${i + 1}`] || '';
          });
        }
      }
      if (!selected) selected = candidates.find((candidate) => candidate.dataset.available === 'true') || candidates[0];
      // The visible choices and the submitted variant must never disagree.
      if (selected) {
        controls.forEach((control, i) => {
          const correctValue = selected.dataset[`option${i + 1}`] || '';
          if (control.value !== correctValue) control.value = correctValue;
        });
      }
      picker.value = selected ? selected.value : '';
      const formInput = section.querySelector('[data-fps-personalization-form] input[name="id"]');
      if (formInput && selected && formInput.value !== selected.value) {
        formInput.value = selected.value;
        formInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
      const feedback = section.querySelector('[data-fps-main-variant-feedback]');
      if (feedback) {
        const unavailable = !selected || selected.dataset.available !== 'true';
        feedback.hidden = !unavailable;
        feedback.textContent = unavailable ? 'Cette variante est épuisée ou indisponible.' : '';
      }
    }

    // Visual buttons are presentation only. Native selects and Shopify variant IDs
    // remain authoritative, so Dawn PDP scripts never need to run here.
    function syncVisualSelection() {
      const realChoices = Array.from(section.querySelectorAll('[data-fps-main-option]'));
      const mainPicker = section.querySelector('select[data-fps-variant="main"]');
      const variants = mainPicker ? Array.from(mainPicker.options) : [];
      for (const button of section.querySelectorAll('[data-fps-visual-option]')) {
        const position = Number(button.dataset.fpsOptionIndex);
        const value = button.dataset.fpsOptionValue || '';
        const control = realChoices.find((item) => Number(item.dataset.fpsOptionIndex) === position);
        const selected = Boolean(control && control.value === value);
        button.classList.toggle('is-selected', selected);
        button.setAttribute('aria-pressed', String(selected));
        const available = variants.some((candidate) =>
          candidate.dataset.available === 'true' && candidate.dataset[`option${position}`] === value);
        button.disabled = !available;
      }

      const boxPicker = section.querySelector('select[data-fps-variant="box"]');
      if (boxPicker) {
        const active = boxPicker.selectedOptions[0];
        for (const button of section.querySelectorAll('[data-fps-box-choice]')) {
          const selected = button.dataset.fpsBoxChoice === boxPicker.value;
          button.classList.toggle('is-selected', selected);
          button.setAttribute('aria-pressed', String(selected));
          const candidate = Array.from(boxPicker.options).find((item) => item.value === button.dataset.fpsBoxChoice);
          button.disabled = !candidate || candidate.dataset.available !== 'true';
        }
        section.querySelectorAll('[data-fps-box-selected]').forEach((label) => {
          label.textContent = active ? active.textContent.replace(/\s+—\s+Épuisé\s*$/, '').trim() : '';
        });
      }
    }

    function collectNativePersonalization() {
      const controls = section.querySelector('[data-fps-product-controls]');
      const root = controls?.querySelector('[data-fhp-personalization]');
      if (!root) {
        if (controls?.dataset.fpsExpectsPersonalization === 'true') {
          tell('Les champs de personnalisation sont indisponibles. Actualisez la page.', 'error');
          return null;
        }
        return {};
      }
      // Fail visibly: a broken or uninitialized field engine must never produce
      // an unpersonalized order for a personalized product.
      if (typeof root.fhpValidatePersonalization !== 'function') {
        tell('La personnalisation ne peut pas être vérifiée. Actualisez la page.', 'error');
        root.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return null;
      }
      if (!root.fhpValidatePersonalization()) return null;
      const properties = {};
      for (const input of root.querySelectorAll('[data-fhp-input]')) {
        const field = input.closest('[data-fhp-field]');
        if (input.disabled || field?.hidden) continue;
        const name = input.getAttribute('name') || '';
        if (!name.startsWith('properties[') || !name.endsWith(']')) continue;
        const value = String(input.value || '').trim();
        if (input.required && !value) {
          tell('Veuillez remplir les champs de personnalisation avant d’ajouter au panier.', 'error');
          input.focus();
          return null;
        }
        if (value) properties[name.slice('properties['.length, -1)] = value;
      }
      return properties;
    }

    // Featured product-gallery behavior is isolated from every other page image.
    const gallery = section.querySelector('[data-fps-gallery]');
    const slides = gallery ? Array.from(gallery.querySelectorAll('[data-fps-gallery-slide]')) : [];
    const thumbs = gallery ? Array.from(gallery.querySelectorAll('[data-fps-gallery-thumb]')) : [];
    const galleryCounter = gallery?.querySelector('[data-fps-gallery-counter]');
    let slideIndex = 0;

    function showSlide(nextIndex) {
      if (!slides.length) return;
      slideIndex = (nextIndex + slides.length) % slides.length;
      slides.forEach((slide, index) => {
        slide.hidden = index !== slideIndex;
      });
      thumbs.forEach((thumb, index) => {
        const selected = index === slideIndex;
        thumb.classList.toggle('is-active', selected);
        thumb.setAttribute('aria-pressed', String(selected));
      });
      if (galleryCounter) galleryCounter.textContent = `${slideIndex + 1} / ${slides.length}`;
      // Scroll only the horizontal THUMBNAIL strip. Calling scrollIntoView here
      // can accidentally jump the entire landing page to the offer on load.
      const track = gallery.querySelector('[data-fps-gallery-thumbs]');
      const active = thumbs[slideIndex];
      if (track && active) {
        const left = active.offsetLeft - track.offsetLeft;
        const right = left + active.offsetWidth;
        if (left < track.scrollLeft) track.scrollTo({ left: left - 8, behavior: 'smooth' });
        else if (right > track.scrollLeft + track.clientWidth) {
          track.scrollTo({ left: right - track.clientWidth + 8, behavior: 'smooth' });
        }
      }
    }

    if (gallery && slides.length) {
      thumbs.forEach((thumb, index) => thumb.addEventListener('click', () => showSlide(index)));
      gallery.querySelector('[data-fps-gallery-prev]')?.addEventListener('click', () => showSlide(slideIndex - 1));
      gallery.querySelector('[data-fps-gallery-next]')?.addEventListener('click', () => showSlide(slideIndex + 1));
      const stage = gallery.querySelector('[data-fps-gallery-stage]');
      stage?.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
          event.preventDefault();
          showSlide(slideIndex + (event.key === 'ArrowRight' ? 1 : -1));
        }
      });
      let touchStartX = null;
      stage?.addEventListener('touchstart', (event) => {
        touchStartX = event.changedTouches?.[0]?.clientX ?? null;
      }, { passive: true });
      stage?.addEventListener('touchend', (event) => {
        if (touchStartX == null) return;
        const delta = (event.changedTouches?.[0]?.clientX ?? touchStartX) - touchStartX;
        if (Math.abs(delta) > 45) showSlide(slideIndex + (delta < 0 ? 1 : -1));
        touchStartX = null;
      }, { passive: true });
      showSlide(0);
    }

    // The featured gallery is intentionally independent of the gift-box color.
    // It contains ONLY the optional uploaded first image and main-product media.
    // Box-color changes update prices/cart IDs, NEVER the photo gallery.
    function applyVariantPhoto(which, selected) {
      if (which !== 'main' || !gallery || !selected?.mediaId) return;
      const slide = slides.find((node) => node.dataset.fpsMediaId === selected.mediaId);
      if (slide) showSlide(slides.indexOf(slide));
    }

    function updatePrices() {
      // The quantity-offer snippet has its own cart, discount-preview and variant logic.
      // Keep the shared gallery, timer and testimonial features, but do not rewrite its prices.
      if (quantityOffer) return { main: variant(section, 'main'), box: null, canAdd: false };
      const main = variant(section, 'main');
      const box = variant(section, 'box');
      const canAdd = Boolean(main.id && box.id && main.available && box.available);
      const mainCompare = Math.max(main.price, main.compareAt || main.price);
      const boxCompare = Math.max(box.price, box.compareAt || box.price);
      const total = main.price + box.price;
      const compareTotal = mainCompare + boxCompare;
      const savings = compareTotal - total;
      const mainSavings = mainCompare - main.price;
      const boxPercent = boxCompare > 0 ? Math.max(0, Math.round(100 * (boxCompare - box.price) / boxCompare)) : 0;

      // Never leave the hero or yellow bar with a missing amount.
      // If Shopify has no compare-at reference in this market, show the real
      // bundle PRICE instead of falsely calling that amount a saving.
      const noSavingsReference = savings <= 0;
      section.querySelectorAll('[data-fps-bundle-savings]').forEach((el) => {
        el.textContent = money(noSavingsReference ? total : savings);
        el.hidden = false;
      });

      const alertMessage = section.querySelector('.fps-alert__message');
      if (alertMessage?.querySelector('[data-fps-bundle-savings]')) {
        const labels = Array.from(alertMessage.children).filter((el) => el.tagName === 'STRONG');
        const start = labels[0];
        const end = labels.length > 1 ? labels.at(-1) : null;
        if (start) {
          if (!start.hasAttribute('data-fps-original')) start.setAttribute('data-fps-original', start.textContent);
          start.textContent = noSavingsReference ? 'OFFRE SPÉCIALE · ENSEMBLE CADEAU À' : start.getAttribute('data-fps-original');
        }
        if (end) {
          if (!end.hasAttribute('data-fps-original')) end.setAttribute('data-fps-original', end.textContent);
          end.textContent = noSavingsReference ? '+ LIVRAISON OFFERTE' : end.getAttribute('data-fps-original');
        }
      }

      const heroHeading = section.querySelector('.hero-copy h1');
      if (heroHeading?.querySelector('[data-fps-bundle-savings]')) {
        const startText = heroHeading.firstChild;
        const emphasis = heroHeading.querySelector('i');
        if (startText?.nodeType === 3) {
          if (!heroHeading.hasAttribute('data-fps-original-start')) heroHeading.setAttribute('data-fps-original-start', startText.textContent);
          startText.textContent = noSavingsReference ? 'Ensemble cadeau à ' : heroHeading.getAttribute('data-fps-original-start');
        }
        if (emphasis) {
          if (!emphasis.hasAttribute('data-fps-original')) emphasis.setAttribute('data-fps-original', emphasis.textContent);
          emphasis.textContent = noSavingsReference ? 'prêt à offrir.' : emphasis.getAttribute('data-fps-original');
        }
      }
      section.querySelectorAll('[data-fps-main-display]').forEach((el) => { el.textContent = displayMoney(el, main.price); });
      section.querySelectorAll('[data-fps-main-strike]').forEach((el) => { el.textContent = displayMoney(el, mainCompare); });
      section.querySelectorAll('[data-fps-main-savings]').forEach((el) => { el.textContent = displayMoney(el, Math.max(0, mainSavings)); });
      section.querySelectorAll('[data-fps-main-strike-wrap],[data-fps-main-savings-wrap]').forEach((el) => { el.hidden = mainSavings <= 0; });

      section.querySelectorAll('[data-fps-bundle-price]').forEach((el) => { el.textContent = displayMoney(el, total); });
      section.querySelectorAll('[data-fps-regular-price]').forEach((el) => {
        el.textContent = displayMoney(el, compareTotal);
        el.hidden = savings <= 0;
      });
      section.querySelectorAll('[data-fps-save-price]').forEach((el) => {
        const prefix = el.getAttribute('data-prefix') || 'ÉCONOMISEZ';
        el.textContent = `${prefix} ${displayMoney(el, Math.max(0, savings))}`;
        el.hidden = savings <= 0;
      });
      section.querySelectorAll('[data-fps-box-display]').forEach((el) => { el.textContent = displayMoney(el, box.price); });
      section.querySelectorAll('[data-fps-box-strike]').forEach((el) => { el.textContent = displayMoney(el, boxCompare); });
      section.querySelectorAll('[data-fps-box-percent]').forEach((el) => { el.textContent = String(boxPercent); });
      section.querySelectorAll('[data-fps-box-percent-wrap],[data-fps-box-strike-wrap]').forEach((el) => {
        el.hidden = boxPercent <= 0;
      });

      // Custom offer cards reuse the same prices, compare references and
      // savings as the original fixed-bundle purchase breakdown.
      const fixedDeal = (price, compare, available, style = 'savings') => ({
        kind: 'deal', price, compare, available, style
      });
      const fixedMetricValues = {
        selection_total: {amount: total, text: money(total), available: true},
        selection_compare: {amount: compareTotal, text: money(compareTotal), available: true},
        selection_savings: {amount: savings, text: money(savings), available: savings > 0},
        selection_percent: {amount: savings, text: `−${compareTotal ? Math.round(savings * 100 / compareTotal) : 0} %`, available: savings > 0},
        main_total: {amount: main.price, text: money(main.price), available: Boolean(main.id)},
        main_compare: {amount: mainCompare, text: money(mainCompare), available: Boolean(main.id)},
        main_savings: {amount: mainSavings, text: money(mainSavings), available: mainSavings > 0},
        main_unit_price: {amount: main.price, text: money(main.price), available: Boolean(main.id)},
        main_unit_compare: {amount: mainCompare, text: money(mainCompare), available: Boolean(main.id)},
        box_total: {amount: box.price, text: money(box.price), available: Boolean(box.id)},
        box_unit_price: {amount: box.price, text: money(box.price), available: Boolean(box.id)},
        box_savings: {amount: boxCompare - box.price, text: money(boxCompare - box.price), available: boxCompare > box.price},
        box_percent: {amount: boxPercent, text: `−${boxPercent} %`, available: boxPercent > 0},
        main_unit_deal: fixedDeal(main.price, mainCompare, Boolean(main.id)),
        box_unit_deal: fixedDeal(box.price, boxCompare, Boolean(box.id), 'percent'),
        selection_deal: fixedDeal(total, compareTotal, Boolean(main.id && box.id))
      };
      section.querySelectorAll('[data-fps-offer-dynamic-metric]').forEach((element) => {
        const metric = fixedMetricValues[element.dataset.fpsOfferDynamicMetric];
        element.hidden = !metric?.available;
        element.classList.toggle('fps-offer-custom-value--deal', Boolean(metric?.available && metric.kind === 'deal'));
        if (element.hidden) { element.replaceChildren(); return; }
        const prefix = element.dataset.fpsOfferDynamicPrefix?.trim();
        const suffix = element.dataset.fpsOfferDynamicSuffix?.trim();
        if (metric.kind !== 'deal') {
          element.textContent = [prefix, metric.text, suffix].filter(Boolean).join(' ');
          return;
        }
        const parts = document.createDocumentFragment();
        if (prefix) parts.append(document.createTextNode(`${prefix} `));
        const price = document.createElement('span');
        price.className = 'fps-offer-custom-value__price';
        price.textContent = money(metric.price);
        parts.append(price);
        if (metric.compare > metric.price) {
          parts.append(document.createTextNode(` ${element.dataset.fpsOfferDynamicWas?.trim() || 'au lieu de'} `));
          const crossed = document.createElement('s');
          crossed.textContent = money(metric.compare);
          parts.append(crossed);
          const last = metric.style === 'percent'
            ? `−${Math.round(100 * (metric.compare - metric.price) / metric.compare)} %`
            : `${element.dataset.fpsOfferDynamicSave?.trim() || 'Économisez'} ${money(metric.compare - metric.price)}`;
          parts.append(document.createTextNode(` · ${last}`));
        }
        if (suffix) parts.append(document.createTextNode(` ${suffix}`));
        element.replaceChildren(parts);
      });

      // Hero only: discount source is selected independently for badge and CTA line.
      // Recalculate when the add-on variant changes; zero-savings claims disappear.
      const offerMetrics = {
        bundle_percent: { saving: savings, text: `−${compareTotal > 0 ? Math.round(100 * savings / compareTotal) : 0} %` },
        bundle_amount: { saving: savings, text: `−${money(savings)}` },
        main_percent: { saving: mainSavings, text: `−${mainCompare > 0 ? Math.round(100 * mainSavings / mainCompare) : 0} %` },
        main_amount: { saving: mainSavings, text: `−${money(mainSavings)}` },
        box_percent: { saving: boxCompare - box.price, text: `−${boxPercent} %` },
        box_amount: { saving: boxCompare - box.price, text: `−${money(boxCompare - box.price)}` }
      };
      section.querySelectorAll('[data-fps-hero-metric]').forEach((element) => {
        const metric = offerMetrics[element.dataset.fpsHeroMetric];
        if (!metric) return;
        element.textContent = metric.text;
        const group = element.closest('[data-fps-hero-metric-group]');
        if (group) group.hidden = metric.saving <= 0;
      });
      for (const button of buttons) {
        button.disabled = adding || !canAdd;
        button.setAttribute('aria-disabled', String(button.disabled));
      }
      return { main, box, canAdd };
    }

    const clocks = Array.from(section.querySelectorAll('[data-fps-clock]'));
    let deadline = announcementDeadline(section);
    function tick() {
      if (!Number.isFinite(deadline)) {
        clocks.forEach((clock) => { clock.hidden = true; });
        section.querySelectorAll('[data-fps-timer-note]').forEach((el) => {
          el.textContent = 'Offre en cours';
        });
        return;
      }
      const remaining = Math.max(0, Math.floor((deadline - Date.now()) / 1000));
      const values = {
        hours: twoDigits(Math.floor(remaining / 3600)),
        minutes: twoDigits(Math.floor((remaining % 3600) / 60)),
        seconds: twoDigits(remaining % 60)
      };
      for (const clock of clocks) {
        clock.hidden = false;
        for (const key of Object.keys(values)) {
          clock.querySelectorAll(`[data-fps-${key}]`).forEach((el) => { el.textContent = values[key]; });
        }
      }
      if (remaining === 0) {
        if ((section.dataset.fpsCountdownMode || 'rolling') === 'rolling') {
          deadline = announcementDeadline(section, true);
          tick();
          return;
        }
        section.querySelectorAll('[data-fps-timer-note]').forEach((el) => {
          el.textContent = 'Le décompte est terminé';
        });
        if (timerId) window.clearInterval(timerId);
      }
    }
    syncMainVariant();
    updatePrices();
    syncVisualSelection();
    tick();
    if (Number.isFinite(deadline) && deadline > Date.now()) timerId = window.setInterval(tick, 1000);

    section.addEventListener('change', (event) => {
      const mainOption = event.target.closest('[data-fps-main-option]');
      if (mainOption) {
        syncMainVariant(mainOption);
        const updated = updatePrices();
        applyVariantPhoto('main', updated.main);
        syncVisualSelection();
        tell('');
        return;
      }
      const picker = event.target.closest('[data-fps-variant]');
      if (picker) {
        const updated = updatePrices();
        applyVariantPhoto(picker.dataset.fpsVariant, updated[picker.dataset.fpsVariant]);
        syncVisualSelection();
        tell('');
      }
    });

    section.addEventListener('click', (event) => {
      const element = event.target instanceof Element ? event.target : event.target?.parentElement;
      const mainButton = element?.closest('[data-fps-visual-option]');
      if (mainButton && section.contains(mainButton)) {
        event.preventDefault();
        if (mainButton.disabled) return;
        const control = Array.from(section.querySelectorAll('[data-fps-main-option]')).find((item) =>
          item.dataset.fpsOptionIndex === mainButton.dataset.fpsOptionIndex);
        if (!control || control.value === mainButton.dataset.fpsOptionValue) return;
        control.value = mainButton.dataset.fpsOptionValue;
        control.dispatchEvent(new Event('change', { bubbles: true }));
        return;
      }
      const boxButton = element?.closest('[data-fps-box-choice]');
      if (boxButton && section.contains(boxButton)) {
        event.preventDefault();
        if (boxButton.disabled) return;
        const picker = section.querySelector('select[data-fps-variant="box"]');
        if (!picker || picker.value === boxButton.dataset.fpsBoxChoice) return;
        picker.value = boxButton.dataset.fpsBoxChoice;
        picker.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });

    section.addEventListener('click', async (event) => {
      const button = event.target.closest('[data-fps-add]');
      if (!button || !section.contains(button)) return;
      event.preventDefault();
      if (adding) return;
      const { main, box, canAdd } = updatePrices();
      if (!canAdd) {
        tell('Veuillez sélectionner des variantes disponibles.', 'error');
        return;
      }

      const personalizedProperties = collectNativePersonalization();
      if (personalizedProperties === null) return;
      const mainLine = { id: Number(main.id), quantity: 1 };
      if (Object.keys(personalizedProperties).length) {
        mainLine.properties = personalizedProperties;
      }
      if (giftNote) {
        const note = giftNote.value.trim();
        if (giftNote.required && !note) {
          giftNote.reportValidity();
          giftNote.focus();
          tell('Veuillez renseigner le champ demandé.', 'error');
          return;
        }
        if (note) {
          const label = section.querySelector(`label[for="${giftNote.id}"]`);
          mainLine.properties = {
            ...(mainLine.properties || {}),
            [label?.textContent?.trim() || 'Message']: note
          };
        }
      }
      adding = true;
      updatePrices();
      tell('Ajout des deux articles au panier…');
      try {
        const response = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({ items: [mainLine, { id: Number(box.id), quantity: 1 }] })
        });
        const result = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(result.description || result.message || 'Impossible de mettre le panier à jour.');
        tell('Les deux produits sont dans votre panier.', 'success');

        if (section.dataset.fpsAfterAdd === 'upcart' && typeof window.upcartOpenCart === 'function') {
          try {
            window.upcartOpenCart();
            adding = false;
            updatePrices();
            return;
          } catch (_) { /* proceed to reliable cart fallback */ }
        }
        const route = section.dataset.fpsAfterAdd === 'checkout' ? 'checkout' : 'cart';
        window.location.assign(root + route);
      } catch (error) {
        adding = false;
        updatePrices();
        tell(error instanceof Error ? error.message : 'Une erreur est survenue.', 'error');
      }
    });

    // Real review carousel: controls only when more testimonials exist than fit.
    const scroller = section.querySelector('[data-fps-reviews]');
    const controls = section.querySelector('[data-fps-review-controls]');
    if (scroller && controls) {
      const prev = controls.querySelector('[data-fps-review-prev]');
      const next = controls.querySelector('[data-fps-review-next]');
      const counter = controls.querySelector('[data-fps-review-pagination]');
      const items = Array.from(scroller.querySelectorAll('.review'));
      const step = () => {
        const first = items[0];
        if (!first) return 1;
        const gap = Number.parseFloat(getComputedStyle(scroller).gap) || 0;
        return first.getBoundingClientRect().width + gap;
      };
      function updateReviewControls() {
        const maximum = Math.max(0, scroller.scrollWidth - scroller.clientWidth);
        controls.hidden = items.length < 2 || maximum < 4;
        if (controls.hidden) return;
        const n = Math.min(items.length, 1 + Math.round(scroller.scrollLeft / step()));
        if (prev) prev.disabled = scroller.scrollLeft < 3;
        if (next) next.disabled = scroller.scrollLeft >= maximum - 3;
        if (counter) counter.textContent = `${n} / ${items.length}`;
      }
      prev?.addEventListener('click', () => scroller.scrollBy({ left: -step(), behavior: 'smooth' }));
      next?.addEventListener('click', () => scroller.scrollBy({ left: step(), behavior: 'smooth' }));
      scroller.addEventListener('scroll', updateReviewControls, { passive: true });
      let obs;
      if (window.ResizeObserver) {
        obs = new ResizeObserver(updateReviewControls);
        obs.observe(scroller);
      }
      window.addEventListener('resize', updateReviewControls);
      requestAnimationFrame(updateReviewControls);
      reviewCleanup = () => {
        window.removeEventListener('resize', updateReviewControls);
        obs?.disconnect();
      };
    }

    section.addEventListener('fps:destroy', () => {
      if (timerId) window.clearInterval(timerId);
      reviewCleanup();
    }, { once: true });
  }

  function boot(container = document) {
    container.querySelectorAll('.fps[data-fps-section]').forEach(init);
    if (container.matches?.('.fps[data-fps-section]')) init(container);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => boot(), { once: true });
  else boot();
  document.addEventListener('shopify:section:load', (event) => boot(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('.fps[data-fps-section]').forEach((section) => {
      section.dispatchEvent(new CustomEvent('fps:destroy'));
    });
  });
})();
